import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Plus, Trash2 } from "lucide-react";
import type {
  ParsedStoryFrame,
  StoriesKeywordHighlight,
  StoriesKeywordStyle,
} from "./stories-parser";

interface Props {
  frame: ParsedStoryFrame;
  onClose: () => void;
  onSave: (next: ParsedStoryFrame) => void;
}

const STYLE_OPTIONS: { value: StoriesKeywordStyle; label: string; sample: string }[] = [
  { value: "red-text", label: "Czerwony", sample: "text-[#ef3e29]" },
  {
    value: "underline",
    label: "Podkreślenie",
    sample: "underline decoration-[#ef3e29] decoration-2",
  },
  { value: "highlight-box", label: "Tło", sample: "bg-[#ef3e29] text-white px-1 rounded" },
  { value: "circle", label: "Kółko", sample: "border border-[#ef3e29] rounded-full px-1.5" },
  {
    value: "arrow-underline",
    label: "Wave",
    sample: "underline decoration-wavy decoration-[#ef3e29]",
  },
];

function KeywordEditor({
  label,
  keywords,
  onChange,
}: {
  label: string;
  keywords: StoriesKeywordHighlight[];
  onChange: (next: StoriesKeywordHighlight[]) => void;
}): JSX.Element {
  const update = (i: number, patch: Partial<StoriesKeywordHighlight>): void => {
    onChange(keywords.map((k, idx) => (idx === i ? { ...k, ...patch } : k)));
  };
  const remove = (i: number): void => onChange(keywords.filter((_, idx) => idx !== i));
  const add = (): void => onChange([...keywords, { keyword: "", style: "red-text" }]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
          {label}
        </label>
        <button
          onClick={add}
          className="flex items-center gap-1 text-[11px] text-[#ef3e29] hover:text-white"
          data-testid="btn-add-keyword"
        >
          <Plus size={12} />
          Dodaj
        </button>
      </div>
      {keywords.length === 0 && (
        <div className="text-[11px] text-white/30 italic">Brak — kliknij "Dodaj"</div>
      )}
      {keywords.map((kw, i) => (
        <div key={i} className="flex gap-1.5 items-center">
          <input
            type="text"
            value={kw.keyword}
            onChange={(e) => update(i, { keyword: e.target.value })}
            placeholder="słowo"
            className="flex-1 bg-white/[0.06] border border-white/[0.08] rounded px-2 py-1 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#ef3e29]"
            data-testid={`input-keyword-${i}`}
          />
          <select
            value={kw.style}
            onChange={(e) => update(i, { style: e.target.value as StoriesKeywordStyle })}
            className="bg-white/[0.06] border border-white/[0.08] rounded px-2 py-1 text-[12px] text-white focus:outline-none focus:border-[#ef3e29]"
            data-testid={`select-style-${i}`}
          >
            {STYLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="bg-zinc-900">
                {o.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => remove(i)}
            className="text-white/40 hover:text-red-400 p-1"
            data-testid={`btn-remove-keyword-${i}`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function EditFrameModal({ frame, onClose, onSave }: Props): JSX.Element {
  const { t } = useTranslation();
  const [headline, setHeadline] = useState(frame.headline);
  const [body, setBody] = useState(frame.body ?? "");
  const [ctaText, setCtaText] = useState(frame.cta?.text ?? "");
  const [headlineKeywords, setHeadlineKeywords] = useState<StoriesKeywordHighlight[]>(
    frame.headlineKeywords ?? []
  );
  const [bodyKeywords, setBodyKeywords] = useState<StoriesKeywordHighlight[]>(
    frame.bodyKeywords ?? []
  );

  const handleSave = (): void => {
    const cleanedHeadline = headlineKeywords.filter((k) => k.keyword.trim().length > 0);
    const cleanedBody = bodyKeywords.filter((k) => k.keyword.trim().length > 0);
    onSave({
      ...frame,
      headline: headline.trim(),
      body: body.trim() || undefined,
      headlineKeywords: cleanedHeadline,
      bodyKeywords: cleanedBody,
      keywordHighlights: [...cleanedHeadline, ...cleanedBody],
      cta: ctaText.trim() ? { ...(frame.cta ?? {}), text: ctaText.trim() } : frame.cta,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-frame-modal-overlay"
    >
      <div
        className="bg-zinc-900 border border-white/[0.08] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-frame-modal"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.08] sticky top-0 bg-zinc-900 z-10">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">
              {t("storiesCreator.edit.title", "Edytuj klatkę")}
            </div>
            <div className="text-sm font-bold text-white">
              {frame.index} · {frame.role.toUpperCase()}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white p-1"
            data-testid="btn-close-edit-modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-white/60 block mb-1.5">
              Nagłówek
            </label>
            <textarea
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              rows={2}
              className="w-full bg-white/[0.06] border border-white/[0.08] rounded px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#ef3e29] resize-none"
              data-testid="textarea-headline"
            />
          </div>

          <KeywordEditor
            label="Wyróżnienia w nagłówku"
            keywords={headlineKeywords}
            onChange={setHeadlineKeywords}
          />

          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-white/60 block mb-1.5">
              Treść (akapity rozdziel pustą linią)
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Pierwszy akapit.&#10;&#10;Drugi akapit.&#10;&#10;Trzeci akapit."
              className="w-full bg-white/[0.06] border border-white/[0.08] rounded px-3 py-2 text-[12px] text-white placeholder-white/20 focus:outline-none focus:border-[#ef3e29] resize-none"
              data-testid="textarea-body"
            />
          </div>

          <KeywordEditor
            label="Wyróżnienia w treści"
            keywords={bodyKeywords}
            onChange={setBodyKeywords}
          />

          {frame.cta && (
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-white/60 block mb-1.5">
                CTA (przycisk)
              </label>
              <input
                type="text"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                className="w-full bg-white/[0.06] border border-white/[0.08] rounded px-3 py-2 text-[13px] text-white focus:outline-none focus:border-[#ef3e29]"
                data-testid="input-cta-text"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-white/[0.08] sticky bottom-0 bg-zinc-900">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-[12px] text-white/70 hover:bg-white/[0.06]"
            data-testid="btn-cancel-edit"
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded text-[12px] font-semibold bg-[#ef3e29] hover:bg-[#dc2f1c] text-white"
            data-testid="btn-save-edit"
          >
            Zapisz
          </button>
        </div>
      </div>
    </div>
  );
}
